"use client";

import { CribNoshLogo } from "@/components/ui/CribNoshLogo";
import {
  useAdaptiveCribnoshAppLaunch,
} from "@/lib/mobile-app-routing";
import type { CribnoshAppId } from "@/lib/mobile-app-links";
import Image from "next/image";

type OpenInAppLandingProps = {
  appId: CribnoshAppId;
  title: string;
  description: string;
  appPath?: string;
  primaryCtaLabel?: string;
  fallbackHref?: string;
  fallbackCtaLabel?: string;
  deepLinkingEnabled?: boolean;
  autoOpenEnabled?: boolean;
};

const APP_ICONS: Record<CribnoshAppId, string> = {
  nosher: "/app-downloads/nosher.png",
  creator: "/app-downloads/creator.png",
  driver: "/app-downloads/driver.png",
};

export function OpenInAppLanding({
  appId,
  title,
  description,
  appPath = "/",
  primaryCtaLabel = "Open app",
  fallbackHref,
  fallbackCtaLabel = "Continue on web",
  deepLinkingEnabled = true,
  autoOpenEnabled = true,
}: OpenInAppLandingProps) {
  const {
    canOpenApp,
    installHref,
    isMobilePlatform,
    openApp,
    platform,
    webFallbackHref,
  } =
    useAdaptiveCribnoshAppLaunch({
      appId,
      appPath,
      webFallbackHref: fallbackHref,
      deepLinkingEnabled,
      autoOpenEnabled,
    });

  const secondaryHref = isMobilePlatform && canOpenApp ? installHref : webFallbackHref;
  const secondaryLabel = isMobilePlatform
    ? canOpenApp
      ? platform === "ios"
        ? "Get it on iPhone"
        : "Get it on Android"
      : fallbackCtaLabel
    : fallbackCtaLabel;
  const primaryLabel = isMobilePlatform && canOpenApp
    ? primaryCtaLabel
    : fallbackCtaLabel;

  return (
    <main className="min-h-screen bg-[#fffcfa] px-6 py-12 text-[#101828]">
      <div className="mx-auto flex w-full max-w-xl flex-col items-center text-center">
        <div className="mb-5 flex items-center gap-3">
          <div className="overflow-hidden rounded-[18px] bg-white ring-1 ring-black/5">
            <Image
              src={APP_ICONS[appId]}
              alt="CribNosh app"
              width={60}
              height={60}
              className="h-[60px] w-[60px] object-cover"
            />
          </div>
          <CribNoshLogo size={120} />
        </div>

        <h1 className="text-3xl font-asgard font-bold">{title}</h1>
        <p className="mt-3 max-w-md text-sm leading-6 text-[#475467]">
          {description}
        </p>

        <div className="mt-8 flex w-full max-w-sm flex-col gap-3">
          <button
            type="button"
            onClick={openApp}
            className="inline-flex w-full items-center justify-center rounded-full bg-[#F23E2E] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#d93527]"
          >
            {primaryLabel}
          </button>

          <a
            href={secondaryHref}
            className="inline-flex w-full items-center justify-center rounded-full px-5 py-3 text-sm font-semibold text-[#094327] transition hover:bg-[#f5fff7]"
          >
            {secondaryLabel}
          </a>
        </div>
      </div>
    </main>
  );
}
